/* eslint-disable camelcase */
import React, { useState, useCallback, useRef, useEffect, memo } from 'react';
import { useForm } from 'react-hook-form';
import { gql, useLazyQuery } from '@apollo/client';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next'; // Import hooka react-i18next
import UrlParamHandler from '../../../utils/urlParamHandler';
import { debounce } from '../../../utils/debounce';
import './Search.scss';
import { ProductProvider } from '../../../context/ProductContext';
import PriceBlock from '../../Product/PriceBlock/PriceBlock';

const GET_PRODUCT = gql`
  query productsSearch($search: String, $pageSize: Int) {
    productsSearch(search: $search, pageSize: $pageSize) {
      __typename
      total_count
      items {
        name
        sku
        canonical_url
        special_price
        advanced_product_inventory {
          __typename
          is_qty_decimal
          use_config_enable_qty_inc
          enable_qty_increments
          use_config_qty_increments
          qty_increments
          use_config_min_sale_qty
          min_sale_qty
          unit
        }
        thumbnail {
          label
          url
        }
        image {
          label
          url
        }
        gallery {
          disabled
          disabled_default
          file
          id
          label
          label_default
          media_type
          medium_image_url
          position
          position_default
          row_id
          small_image_url
          url
          value_id
        }
        guarantees {
          guarantee_description
          guarantee_img_url
        }
        price_last_30
        prices {
          amount
          unit
          bg_color
          currency_symbol
          label
          description
          old_amount
          percentage
          price_type
          promotion_start
          promotion_end
          txt_color
        }
        ... on GroupedProduct {
          items {
            product {
              gallery {
                disabled
                disabled_default
                file
                id
                label
                label_default
                large_image_url
                media_type
                medium_image_url
                position
                position_default
                row_id
                small_image_url
                url
                value_id
                is_hover
                is_thumbnail
              }
              price_last_30
              prices {
                price_type
                amount
                bg_color
                currency_symbol
                label
                unit
                old_amount
                percentage
                promotion_start
                promotion_end
                txt_color
                description
              }
            }
          }
        }
      }
    }
  }
`;

const Search = ({ hideSearch }) => {
  const { handleSubmit } = useForm();
  const location = useLocation();
  // Add a defensive check to handle SSR and avoid errors with location
  const initialSearch = location && location.search ? 
    (UrlParamHandler(location.search).queryParams?.q || '') : 
    '';
  const [search, setSearch] = useState(initialSearch);
  const [isClicked, setIsClicked] = useState(false);
  const count = useRef(-1);
  const navigate = useNavigate();
  const { t } = useTranslation(); // Używamy hooka useTranslation

  // Add error handling to handle potential Apollo client issues
  const [fetchSearch, { data, loading, error }] = useLazyQuery(GET_PRODUCT, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: false,
    onError: (err) => console.error('Search query error:', err)
  });
  
  // Create a stable debounced function that doesn't change on rerenders
  const delayedFetch = useCallback(
    debounce((searchTerm) => {
      if (searchTerm.length > 1) {
        fetchSearch({ variables: { search: searchTerm, pageSize: 5 } });
      }
    }, 500),
    [fetchSearch]
  );
  
  // Prevent recreating this function on every render
  const onChange = useCallback((e) => {
    const value = e.target.value;
    setSearch(value);
    setIsClicked(false);
    if (value.length > 1) {
      delayedFetch(value);
    }
  }, [delayedFetch]);

  // Use ref to prevent unnecessary re-renders during keyboard navigation
  const searchInputRef = useRef(null);
  
  const selectSuggestion = useCallback((e) => {
    if (!data?.productsSearch?.items?.length) return;
    
    // Prevent event propagation to stop unnecessary search fetches during navigation
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      
      if (e.key === 'ArrowDown') {
        count.current = (count.current + 1) % data.productsSearch.items.length;
      } else if (e.key === 'ArrowUp') {
        count.current = count.current === -1 || count.current === 0
            ? data.productsSearch.items.length - 1
            : count.current - 1;
      }
      
      // Update the input value using the ref instead of state to prevent triggering a re-render
      if (searchInputRef.current) {
        const name = data.productsSearch.items[count.current].name;
        searchInputRef.current.value = name;
        setSearch(name);
      }
    } else if (e.key === 'Enter' && count.current >= 0) {
      // Only hide search on Enter key when an item is selected
      if (typeof hideSearch === 'function') {
        hideSearch();
      }
    }
  }, [data, hideSearch]);

  const onSubmit = useCallback(() => {
    if (search.length) {
      navigate({ pathname: '/catalogsearch/result', search: `?q=${encodeURIComponent(search)}` });
      if (typeof hideSearch === 'function') {
        hideSearch();
      }
    }
  }, [search, navigate, hideSearch]);

  // Use ref to track if component is mounted to prevent unnecessary queries/updates
  const isMounted = useRef(false);
  
  // Load initial search data if needed
  useEffect(() => {
    if (initialSearch && initialSearch.length > 1 && !isMounted.current) {
      isMounted.current = true;
      delayedFetch(initialSearch);
    }
  }, [initialSearch, delayedFetch]);
  
  // Handle clicks outside search
  useEffect(() => {
    // Check if we're in a browser environment
    if (typeof document !== 'undefined') {
      const handleClickOutside = (e) => {
        // Only set isClicked if the click is outside the search component
        if (searchInputRef.current && !searchInputRef.current.contains(e.target)) {
          setIsClicked(true);
        }
      };
      
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
    return undefined;
  }, []);

  const getThumbnail = useCallback((gallery, thumbnail) => {
    const galleryThumbnail = gallery.find((x) => x.position === 1);
    return galleryThumbnail?.small_image_url || thumbnail.url;
  }, []);

  return (
      <form onSubmit={handleSubmit(onSubmit)} className="search">
        <div className="search__field">
          <button type="submit" title="Traži" className="search__btn search_icon" aria-label="Search" />
          <input
              ref={searchInputRef}
              autoComplete="off"
              type="text"
              className="search__input"
              name="search"
              value={search}
              onChange={onChange}
              onKeyDown={selectSuggestion} // Changed from onKeyUp to onKeyDown for better control
              placeholder={t('header.searchPlaceholder')}
          />
          <button type="submit" title="Traži" className="search__btn search__btn_search" onClick={onSubmit}>
            {t('header.searchButton')}
          </button>
        </div>

        <div
            className="search__autocomplete search-autocomplete"
            id="search-autocomplete"
            style={{ display: search.length > 1 && !loading && !isClicked ? 'block' : 'none' }}
        >
          <div className="smile-elasticsuite-autocomplete-result">
            <dl role="listbox" className="autocomplete-list">
              {search.toLowerCase() === 'dyson' && !loading && !isClicked && (
                  <a href="/dyson-demozone-new" className="dyson-link">
                    <dd role="option" id="qs-option-0">
                      <div className="product-image-box">
                        <img
                            width="45"
                            height="auto"
                            src="https://s3.emmezeta.hr/media/thumbs/wysiwyg/Dyson/thumbnail_dyson_brand_store.png"
                            alt="Dyson Brand Store"
                        />
                      </div>
                      <div>
                        <div className="product-primary">
                          <div className="product-name">DYSON BRAND STORE</div>
                        </div>
                        <div className="clear" />
                      </div>
                    </dd>
                  </a>
              )}
              {data?.productsSearch?.items?.map((product, index) => (
                  <ProductProvider value={product} key={product.sku}>
                    <Link to={`/${product.canonical_url}`}>
                      <dd role="option" id={`qs-option-${index}`}>
                        <div className="product-image-box">
                          <img
                              width="45"
                              height="auto"
                              src={getThumbnail(product.gallery, product.thumbnail)}
                              alt={product.thumbnail.label}
                          />
                        </div>
                        <div className="product-shop product-item">
                          <div className="f-fix">
                            <div className="product-primary">
                              <div className="product-name">{product.name}</div>
                            </div>
                            <div className="product-secondary">
                              <div className="price-box">
                                <PriceBlock product={product} hideLast30 hideLabels={false} />
                              </div>
                            </div>
                          </div>
                          <div className="clear" />
                        </div>
                      </dd>
                    </Link>
                  </ProductProvider>
              ))}
              {data?.productsSearch?.items?.length === 0 && (
                  <div>{t('searchResult.noResults')}</div>
              )}
            </dl>
          </div>
        </div>
      </form>
  );
};

export default memo(Search);